import { EmojiPicker, NativeRenderer, createPicker, i18n } from "picmo";
import { PnPHelpers } from "../../helpers/pnp-helpers";
import { TraceLoggingHelpers } from "../../helpers/trace-logging-helpers";
import { IChartContainer } from "../../models/base/chart-container.interface";
import { EmojiModel } from "../../models/drawing-tools/emoji.model";
import { TiChartMenuContainer } from "../../ti-chart-menu.container";
import { IComponent } from "../component.interface";
import { StringHelpers } from "../../helpers/string-helpers";

export class ToolIconComponent implements IComponent {
    public id = StringHelpers.newGuid();

    private tiChartMenuContainer: TiChartMenuContainer;
    private base: IChartContainer;
    private element: HTMLElement | null;

    private firstLevelOpen = false;
    private secondLevelOpen = false;

    private customEmojiData: Array<EmojiModel> = [];
    private emojiPicker: EmojiPicker | null = null;
    private addEmojiControlPopup: HTMLElement | null;
    private addedComponentReady = false;
    private emojiIconReady = false;

    constructor(tiChartMenuContainer: TiChartMenuContainer, base: IChartContainer) {
        this.tiChartMenuContainer = tiChartMenuContainer;
        this.base = base;

        this.subscribeEmojiData();
        this.onResize();
        this.onBodyClick();
        this.onEscPress();
    }

    private subscribeEmojiData() {
        this.base.chartService.subscribeEmojiData((emojiData: Array<EmojiModel>) => {
            this.customEmojiData = emojiData;
            this.emojiIconReady = true;
            this.addEmojiControl();
        });
    }

    public show() {
        if (this.element) {
            this.firstLevelOpen = true;
            this.element.classList.add('show');
            this.setRelatedElementClass();
        }
    }

    public hide() {
        if (this.element) {
            this.firstLevelOpen = false;
            this.element.classList.remove('show');
            this.hideSubMenu();
            if (this.addEmojiControlPopup?.classList.contains('active')) {
                this.hideAddEmojiControl();
            }
            this.setRelatedElementClass();
        }
    }

    public hideSubMenu() {
        if (!this.element) {
            return;
        }

        this.element.querySelectorAll<HTMLElement>('.highcharts-active').forEach((el: HTMLElement) => {
            el.classList.remove('highcharts-active');
        });
        this.element.querySelectorAll<HTMLElement>('.highcharts-current').forEach((el: HTMLElement) => {
            el.classList.remove('highcharts-current');
        });
        this.element.querySelectorAll<HTMLElement>('.highcharts-submenu-wrapper').forEach((sub: HTMLElement) => {
            sub.style.display = 'none';
        });

        this.secondLevelOpen = false;
    }

    public addToolIconComponents(show: boolean) {
        this.element = this.base.tiChartSeries.element.findFirst('.highcharts-stocktools-wrapper');
        const existElement = this.element?.findFirst('.highchart-custom-toggle-toolbar');
        if (!existElement) {
            const div = document.createElement('div');
            div.classList.add('highchart-custom-toggle-toolbar');
            div.addEventListener('click', () => {
                this.toggleTools(!this.tiChartMenuContainer.displayTools);
            });

            this.element?.insertAdjacentElement('beforeend', div);

            if (show) {
                this.toggleTools(show);
            }
        }

        if (this.base.marketingWebChartMode) {
            setTimeout(() => {
                if (this.element) {
                    this.element.style.display = 'block';
                }
            }, 300);
        }
        this.addedComponentReady = true;
        this.addEmojiControl();
        this.onMenuScroll();
        this.onMenuClick();
    }

    private addEmojiControl() {
        if (!this.addedComponentReady || !this.emojiIconReady) {
            return;
        }

        let chartWrapper = null;

        if (this.base.marketingWebChartMode) {
            chartWrapper = this.base.element.closest('.web-charts-marketing-wrapper');
        }

        if (!chartWrapper) {
            chartWrapper = this.base.element.querySelector('.main-chart');
        }

        if (!chartWrapper) {
            return;
        }

        if (this.addEmojiControlPopup) {
            return;
        }

        const emojiWrapper = document.createElement('div');
        emojiWrapper.className = 'add-emoji-control-popup';

        const emojiContainer = document.createElement('div');
        emojiContainer.className = 'quick-add-emoji-picker-container';

        emojiWrapper.appendChild(emojiContainer);
        chartWrapper.appendChild(emojiWrapper);

        this.addEmojiControlPopup = emojiWrapper;

        const customEmojis = this.customEmojiData;
        const categories = [
            'smileys-emotion',
            'activities',
            'animals-nature',
            'flags',
            'food-drink',
            'objects',
            'people-body',
            'symbols',
            'travel-places'
        ]

        if (customEmojis.length) {
            categories.unshift('custom');
        }
        categories.unshift('recents');

        const dictionary = i18n;
        dictionary.en["categories.custom"] = 'Stock';

        const emojiPickerOptions: any = {
            showRecents: true,
            showVariants: false,
            showPreview: false,
            showSearch: true,
            custom: customEmojis,
            categories: categories as any,
            renderer: new NativeRenderer(),
            locale: 'en',
            i18n: dictionary.en,
            rootElement: emojiContainer
        };

        this.setEmojiOptionSize(emojiPickerOptions);
        const picker = createPicker(emojiPickerOptions);
        this.emojiPicker = picker;

        this.addEmojiControlPopup.classList.add('inited');

        const updateCustomEmoji = () => {
            emojiContainer.querySelectorAll('.picmo__emojiCategory').forEach((el: Element, i: number) => {
                if (i < 2) {
                    el.querySelectorAll('button').forEach((b: Element) => {
                        const emoji = b.getAttribute('data-emoji');
                        b.innerHTML = `<span>${emoji}</span>`
                    });
                }
            });
        };

        picker.addEventListener('data:ready', () => {
            TraceLoggingHelpers.log('Picker Ready: ', picker);

            emojiContainer.querySelector<HTMLElement>('.picmo__categoryButton')?.click();
            setTimeout(() => {
                updateCustomEmoji();
            }, 150)

            emojiContainer.querySelectorAll('svg[data-icon="robot"]').forEach((el: Element) => {
                el.setAttribute('viewBox', '0 0 448 512');
                el.innerHTML = '<path d="M160 80c0-26.5 21.5-48 48-48h32c26.5 0 48 21.5 48 48V432c0 26.5-21.5 48-48 48H208c-26.5 0-48-21.5-48-48V80zM0 272c0-26.5 21.5-48 48-48H80c26.5 0 48 21.5 48 48V432c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V272zM368 96h32c26.5 0 48 21.5 48 48V432c0 26.5-21.5 48-48 48H368c-26.5 0-48-21.5-48-48V144c0-26.5 21.5-48 48-48z"></path>';
            });
        });

        picker.addEventListener('emoji:select', (selection) => {
            TraceLoggingHelpers.log('Selected quick-add-emoji: ', selection.emoji);
            this.hideAddEmojiControl(true);
            setTimeout(() => {
                updateCustomEmoji();
            }, 150)


            if (this.base.drawingToolsManager) {
                this.base.drawingToolsManager.setPreselectEmoji(selection.emoji);
                this.base.drawingToolsManager.emojiAddingMode = true;
                this.base.drawingToolsManager.addRootDrawMode(selection.emoji);
            }
        });
    }

    private setEmojiOptionSize(emojiPickerOptions: any) {
        if (window.matchMedia('(max-width: 360px)').matches) {
            emojiPickerOptions.emojisPerRow = 7;
            emojiPickerOptions.emojiSize = '1rem';
        } else if (window.matchMedia('(max-width: 640px)').matches) {
            emojiPickerOptions.emojisPerRow = 7;
            emojiPickerOptions.emojiSize = '1.2rem';
        } else {
            emojiPickerOptions.emojiSize = '2rem';
        }
    }

    private onMenuClick() {
        this.element?.findParents('.main-chart')?.querySelectorAll<HTMLElement>('.highcharts-stocktools-toolbar > li').forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                if (el.classList.contains('highcharts-emoji-custom-annotation')) {
                    if (this.addEmojiControlPopup) {
                        if (this.addEmojiControlPopup.classList.contains('active')) {
                            this.hideAddEmojiControl();
                        } else {
                            this.showAddEmojiControl();
                        }
                    }
                } else {
                    this.hideAddEmojiControl();
                }
            });

            const submenuArrow = el.querySelector('.highcharts-submenu-item-arrow');
            if (submenuArrow) {
                submenuArrow.addEventListener('click', () => {
                
                    this.hideAddEmojiControl();
    
                    setTimeout(() => {
                        const submenu = el.findFirst('.highcharts-submenu-wrapper');
                        const isActive = submenu.style.display == 'block';
                        this.secondLevelOpen = isActive;
                        if (this.secondLevelOpen && this.base.marketingWebChartMode) {
                            submenu.style.left = '43px';
                        }
                        this.setRelatedElementClass();
                    }, 300);
                });
            }

            el.querySelectorAll<HTMLElement>('.highcharts-submenu-wrapper > li').forEach((sub: HTMLElement) => {
                sub.addEventListener('click', () => {
                    this.hideAddEmojiControl();

                    this.secondLevelOpen = false;
                    this.setRelatedElementClass();
                });
            });
        });
    }

    private setRelatedElementClass() {
        const customToggleToolbar = this.element?.findFirst('.highchart-custom-toggle-toolbar');
        const menuWrapper = this.element?.findFirst('.highcharts-menu-wrapper');
        const tooltip = this.element?.findParents('.main-chart')?.findFirst('.tooltip');
        let pnp = null;
        if (!this.base.marketingWebChartMode) {
            pnp = document.body.findFirst('.ti-chart.pnp');
        } else {
            pnp = this.base.element.parentElement?.findFirst('.ti-chart.pnp');
        }

        if (!this.firstLevelOpen) {
            tooltip?.removeClass('level1');
            tooltip?.removeClass('level2');

            pnp?.removeClass('level1');
            pnp?.removeClass('level2');

            customToggleToolbar?.removeClass('level1');
            customToggleToolbar?.removeClass('level2');

            menuWrapper?.removeClass('level1');
            menuWrapper?.removeClass('level2');
        } else {
            tooltip?.addClass('level1');
            pnp?.addClass('level1');
            customToggleToolbar?.addClass('level1');
            menuWrapper?.addClass('level1');

            if (!this.secondLevelOpen) {
                tooltip?.removeClass('level2');
                pnp?.removeClass('level2');
                customToggleToolbar?.removeClass('level2');
                menuWrapper?.removeClass('level2');
            } else {
                tooltip?.addClass('level2');
                pnp?.addClass('level2');
                customToggleToolbar?.addClass('level2');
                menuWrapper?.addClass('level2');
            }
        }

        if (pnp && pnp.containClass('show') && !pnp.containClass('right')) {
            PnPHelpers.setExtraLeft(pnp);            
        }
    }

    private isDrag = false;
    private onMenuScroll() {
        const menuWrapper = this.element?.findFirst('.highcharts-menu-wrapper');
        if (!menuWrapper) return;

        if (menuWrapper.classList.contains('inited')) {
            return;
        }

        menuWrapper.classList.add('inited');

        let pos = { top: 0, y: 0 };
        
        this.isDrag = false;
        menuWrapper.addEventListener('mousedown', (e: any) => {
            pos = {
                top: menuWrapper.scrollTop,
                y: e.clientY,
            };
            this.isDrag = true;
        });

        menuWrapper.addEventListener('mousemove', (e: any) => {
            if (!this.isDrag) {
                return;
            }

            const dy = e.clientY - pos.y;

            // Scroll the element
            menuWrapper.scrollTop = pos.top - dy;
        });
        menuWrapper.addEventListener('mouseleave', () => {
            this.isDrag = false;
        });
    }

    private toggleTools(show: boolean) {
        this.tiChartMenuContainer.displayTools = show;
        this.tiChartMenuContainer.displayTimeframe = false;
        this.tiChartMenuContainer.displayIndicator = false;
        this.tiChartMenuContainer.displayChartType = false;
        this.tiChartMenuContainer.displayTradeOrderType = false;
        this.tiChartMenuContainer.toggleControls();

        if (show) {
            this.base.hideTooltip();
        }
    }

    private setEmojiControlAfterResize() {
        if (!this.addEmojiControlPopup) {
            return;
        }

        const emojiOptions: any = {};
        this.setEmojiOptionSize(emojiOptions);
        this.emojiPicker?.updateOptions(emojiOptions);

        if (this.base.element.clientHeight <= 540) {
            this.addEmojiControlPopup.style.top = '78px';
        } else {
            this.addEmojiControlPopup.style.top = '';
        }
    }

    private resizeTimer: any;
    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            this.addEmojiControlPopup?.classList.remove('active');

            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                this.setEmojiControlAfterResize();
            }, 500);
        });
    }

    private removeRootDrawModeTimer: any;
    private showAddEmojiControl() {
        this.addEmojiControlPopup?.classList.add('active');
        document.querySelector<HTMLElement>('.picmo__clearSearchButton')?.click();
        const chart: any = this.base.tiChartSeries?.highStockChart;
        if (chart) {
            chart.navigationBindings.deselectAnnotation();
        }

        if (this.base.drawingToolsManager) {
            this.base.drawingToolsManager.emojiAddingMode = true;
            this.base.drawingToolsManager.setPreselectEmoji('');

            clearTimeout(this.removeRootDrawModeTimer);
            this.removeRootDrawModeTimer = setTimeout(() => {
                this.base.drawingToolsManager.removeRootDrawMode(true);
            }, 300);
        }
    }

    private hideAddEmojiControl(adding?: boolean) {
        this.addEmojiControlPopup?.classList.remove('active');
        const chart: any = this.base.tiChartSeries?.highStockChart;
        if (chart) {
            chart.navigationBindings.deselectAnnotation();
        }
        if (this.base.drawingToolsManager) {
            this.base.drawingToolsManager.setPreselectEmoji('');
            this.base.drawingToolsManager.removeRootDrawMode(adding == true);
        }
    }

    private onBodyClick() {
        this.base.domEvents.onMouseUp(this.id, () => {
            this.isDrag = false;
        });
        this.base.domEvents.onBodyClick(this.id, (e: any) => {
            const element = e.target as HTMLElement;
            TraceLoggingHelpers.log('element 1', element.closest('.highcharts-emoji-custom-annotation'))
            TraceLoggingHelpers.log('element 2', element.closest('.add-emoji-control-popup'))
            if (!element || (!element.closest('.highcharts-emoji-custom-annotation') && !element.closest('.add-emoji-control-popup'))) {
                if (this.base.drawingToolsManager?.hasPreselectEmoji() || this.addEmojiControlPopup?.classList.contains('active') || this.base.drawingToolsManager?.emojiAddingMode) {
                    TraceLoggingHelpers.log('onBodyClick hideAddEmojiControl')
                    this.hideAddEmojiControl();
                }
            }
        });
    }

    private onEscPress() {
        this.base.domEvents.onEscPress(this.id, () => {
            if (this.base.drawingToolsManager?.hasPreselectEmoji()) {
                this.hideAddEmojiControl();
            }
        });
    }
}
